import { useCallback, useEffect, useRef } from 'react';

import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';

import { useFireAnalyticsEvent } from './use-fire-analytics';

const DWELL_TIME_MS = 5000;
const ANALYTICS_SOURCE = 'rovoInsights';
// `enablePlatformEvents` adds `atlaskit`; only pass feature-specific tags here.
const AI_EVENT_ADDITIONAL_TAGS: string[] = ['aiMate'];

export type InsightsInvokedFrom =
	| 'inlineInsightsButton'
	| 'insightsNotification'
	| 'insightsHomeBanner'
	| 'weeklyInsights';

interface UseInsightsAnalyticsStateProps {
	invokedFrom?: InsightsInvokedFrom;
}

interface UseInsightsAnalyticsStateReturn {
	onFeedContentDisplayed: (attributes: {
		insightCount: number;
		groupCount: number;
		insightTypes: string[];
	}) => void;
	onInsightCardClicked: (insightCategory: string) => void;
	onUrlClicked: (url: string) => void;
	onRetryClicked: () => void;
}

/**
 * Stateful analytics hook for Rovo Insights.
 *
 * Manages AI unified instrumentation events (`aiResult viewed` / `aiResult actioned`)
 * with MAU-qualifying logic per DE/DS requirements.
 */
export const useInsightsAnalyticsState = ({
	invokedFrom = 'weeklyInsights',
}: UseInsightsAnalyticsStateProps = {}): UseInsightsAnalyticsStateReturn => {
	const mauQualifiedViewFired = useRef(false);
	const interactionInitFired = useRef(false);
	const dwellTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const { fireAnalyticsEvent } = useFireAnalyticsEvent();

	const { trackAIInteractionInit, trackAIResultView, trackAIResultAction } =
		useAIEventsInstrumentation({
			product: 'rovo',
			subproduct: 'rovoInsights',
			aiFeatureName: 'rovoInsights',
			proactiveGeneratedAI: 1,
			userGeneratedAI: 0,
			source: ANALYTICS_SOURCE,
			enablePlatformEvents: true,
			tags: AI_EVENT_ADDITIONAL_TAGS,
		});

	useEffect(() => {
		return () => {
			if (dwellTimerRef.current) {
				clearTimeout(dwellTimerRef.current);
			}
		};
	}, []);

	const fireAIInteractionInitIfNeeded = useCallback((): void => {
		if (!interactionInitFired.current) {
			interactionInitFired.current = true;
			trackAIInteractionInit({
				attributes: {
					invokedFrom,
				},
			});
		}
	}, [trackAIInteractionInit, invokedFrom]);

	const fireMAUViewedIfNeeded = useCallback(
		(mauEligibilityCriteria: string): void => {
			if (!mauQualifiedViewFired.current) {
				mauQualifiedViewFired.current = true;
				fireAIInteractionInitIfNeeded();
				trackAIResultView({
					doesNotMeetMAUCriteria: false,
					attributes: {
						mauEligibilityCriteria,
						invokedFrom,
					},
				});
			}
		},
		[fireAIInteractionInitIfNeeded, trackAIResultView, invokedFrom],
	);

	const onFeedContentDisplayed = useCallback(
		(attributes: { insightCount: number; groupCount: number; insightTypes: string[] }): void => {
			fireAIInteractionInitIfNeeded();
			trackAIResultView({
				doesNotMeetMAUCriteria: true,
				attributes: {
					invokedFrom,
					...attributes,
				},
			});

			if (!dwellTimerRef.current && !mauQualifiedViewFired.current) {
				dwellTimerRef.current = setTimeout(() => {
					dwellTimerRef.current = null;
					fireMAUViewedIfNeeded('dwellTimeRealized');
				}, DWELL_TIME_MS);
			}
		},
		[fireAIInteractionInitIfNeeded, trackAIResultView, invokedFrom, fireMAUViewedIfNeeded],
	);

	const onInsightCardClicked = useCallback(
		(insightCategory: string): void => {
			fireMAUViewedIfNeeded('insightsCardClicked');

			trackAIResultAction('insightsCardClicked', {
				attributes: {
					invokedFrom,
					insightCategory,
				},
			});

			fireAnalyticsEvent({
				eventType: 'ui',
				actionSubject: 'insightCard',
				action: 'clicked',
				attributes: {
					insightCategory,
				},
			});
		},
		[fireMAUViewedIfNeeded, trackAIResultAction, invokedFrom, fireAnalyticsEvent],
	);

	const onUrlClicked = useCallback(
		(url: string): void => {
			fireMAUViewedIfNeeded('outputUrlClicked');

			trackAIResultAction('outputUrlClicked', {
				attributes: {
					invokedFrom,
					url,
				},
			});
		},
		[fireMAUViewedIfNeeded, trackAIResultAction, invokedFrom],
	);

	const onRetryClicked = useCallback((): void => {
		fireAnalyticsEvent({
			eventType: 'ui',
			actionSubject: 'insightsFeedRetryButton',
			action: 'clicked',
		});
	}, [fireAnalyticsEvent]);

	return {
		onFeedContentDisplayed: onFeedContentDisplayed,
		onInsightCardClicked: onInsightCardClicked,
		onUrlClicked: onUrlClicked,
		onRetryClicked: onRetryClicked,
	};
};
